/* eslint-disable no-alert */
import React from 'react';
import PropTypes from 'prop-types';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import shortid from 'shortid';

function Pagination({ onChange, data, small, showPagesLabel, disabled }) {
  const handlePrev = () => {
    if (disabled) return;
    const newPage = data.current_page - 1;
    if (newPage <= 0) return;
    onChange((state) => ({ ...state, page: newPage }));
  };

  const handleNext = () => {
    if (disabled) return;
    const newPage = data.current_page + 1;
    if (newPage > data.total_pages) return;
    onChange((state) => ({ ...state, page: newPage }));
  };

  const handleSetPage = (newPage) => () => {
    if (disabled) return;
    onChange((state) => ({ ...state, page: newPage }));
  };

  const handleSetPromptPage = () => {
    const page = parseInt(
      prompt(
        `Enter page (between 1 and ${data.total_pages || '1'})`,
        data.current_page || '1'
      ),
      10
    );
    if (!page) {
      alert('Invalid page number!');
      return;
    }
    if (page < 1 || page > data.total_pages) {
      alert('Page value not in range!');
      return;
    }
    if (page !== data.current_page) onChange(page);
  };

  return (
    <div
      className={`${
        showPagesLabel ? 'flex items-center justify-between w-full' : ''
      }`}
    >
      {showPagesLabel ? (
        <span className="text-gray-600 font-semibold">
          {data?.total_pages} Page(s)
        </span>
      ) : null}
      <div className="inline-flex justify-between items-center border rounded bg-white gap-5">
        <button
          className="pagination-btn border-r"
          type="button"
          onClick={handlePrev}
          disabled={data.current_page === 1 || disabled}
        >
          <FaAngleLeft
            className={`text-xl ${
              data.current_page === 1 || disabled
                ? 'text-gray-50'
                : 'text-gray-500'
            }`}
          />
        </button>
        {!small && <span className="text-gray-600">PREV</span>}

        {data.total_pages > 4 &&
          !(data.current_page + 3 > data.total_pages) &&
          !(data.total_pages - 3 <= data.current_page) && (
            <>
              {[0, 1, 2, 3].map((i) => (
                <div key={shortid.generate()}>
                  <button
                    type="button"
                    key={i}
                    className={`${
                      !i ? 'text-gray-600 font-semibold' : 'text-gray-400'
                    } hover:text-gray-600`}
                    onClick={handleSetPage(data.current_page + i)}
                  >
                    {data.current_page + i}
                  </button>
                </div>
              ))}
              {data.current_page + 3 < data.total_pages && (
                <>
                  <button
                    type="button"
                    className="text-gray-600"
                    onClick={handleSetPromptPage}
                  >
                    ...
                  </button>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600"
                    onClick={handleSetPage(data.total_pages)}
                  >
                    {data.total_pages}
                  </button>
                </>
              )}
            </>
          )}

        {data.total_pages > 4 && data.total_pages - 3 <= data.current_page && (
          <>
            {data.total_pages - 4 > 1 && (
              <>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600"
                  onClick={handleSetPage(1)}
                >
                  1
                </button>
                <button
                  type="button"
                  className="text-gray-600"
                  onClick={handleSetPromptPage}
                >
                  ...
                </button>
              </>
            )}
            {[0, 1, 2, 3].map((i) => (
              <button
                type="button"
                key={i}
                className={`${
                  data.current_page === data.total_pages - 3 + i
                    ? 'text-gray-600 font-semibold'
                    : 'text-gray-400 font-light'
                } hover:text-gray-600`}
                onClick={handleSetPage(data.total_pages - 3 + i)}
              >
                {data.total_pages - 3 + i}
              </button>
            ))}
          </>
        )}

        {data.total_pages <= 4 && (
          <>
            {Array(data.total_pages)
              .fill()
              .map((_, i) => (
                <div key={shortid.generate()}>
                  <button
                    type="button"
                    className={`text-base ${
                      data.current_page === i + 1
                        ? 'text-gray-600 font-semibold'
                        : 'text-gray-400 font-light'
                    } hover:text-gray-600`}
                    onClick={handleSetPage(i + 1)}
                    disabled={data.total_pages === 1}
                  >
                    {i + 1}
                  </button>
                </div>
              ))}
          </>
        )}

        {!small && <span className="text-gray-600">NEXT</span>}
        <button
          className="pagination-btn border-l"
          type="button"
          onClick={handleNext}
          disabled={data.current_page >= data.total_pages || disabled}
        >
          <FaAngleRight
            className={`text-xl ${
              data.current_page >= data.total_pages || disabled
                ? 'text-gray-50'
                : 'text-gray-500'
            }`}
          />
        </button>
      </div>
    </div>
  );
}

Pagination.defaultProps = {
  onChange: () => {},
  data: {
    current_page: 1,
    total_pages: 1,
    total: 1,
  },
  small: false,
  disabled: false,
  showPagesLabel: false,
};

Pagination.propTypes = {
  onChange: PropTypes.func,
  data: PropTypes.shape({
    current_page: PropTypes.number,
    total_pages: PropTypes.number,
    total: PropTypes.number,
  }),
  small: PropTypes.bool,
  disabled: PropTypes.bool,
  showPagesLabel: PropTypes.bool,
};

export default Pagination;
